import { Clock, FileText, MapPin, ShieldCheck, TrendingUp, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { cn } from "@/lib/utils";

interface LifecycleItem {
  label: string;
  title: string;
  body: string;
  icon: LucideIcon;
  pillBg: string;
}

const LIFECYCLE: LifecycleItem[] = [
  {
    label: "Buy",
    title: "True love, or a fling?",
    body: "You've got 15 days to find out. Maybe 30. We're tracking the window either way — no judgment.",
    icon: Clock,
    pillBg: "#0F766E",
  },
  {
    label: "Own",
    title: "Think of the children.",
    body: "Warranties run out. Recalls happen. That car seat you bought in 2021? Could've been recalled while you were just living your life. Our warranty tracker checks every day.",
    icon: ShieldCheck,
    pillBg: "#0D6B62",
  },
  {
    label: "Use",
    title: "The good bit.",
    body: "You lend your drill to your brother-in-law. That was 18 months ago. Tag things, track who's got what, note where stuff actually lives. Not saying you'll get the drill back. But at least you'll know where to look.",
    icon: MapPin,
    pillBg: "#0B5E57",
  },
  {
    label: "Sell",
    title: "Wait, someone would pay 300 bucks for this?",
    body: "You list it for $50. Your phone explodes. It was worth way more. We track what your stuff is actually worth, so you know before the lowballers do.",
    icon: TrendingUp,
    pillBg: "#09524C",
  },
  {
    label: "Claim",
    title: "Oof. This could take a while.",
    body: "The adjuster wants a list. With serial numbers. And photos. And prices you paid three years ago. We make the PDF. You're still going to be on hold, but you'll sound like you've got it together.",
    icon: FileText,
    pillBg: "#074541",
  },
];

export default function LifecycleSection() {
  const { revealed, latest, setRef } = useScrollReveal(LIFECYCLE.length);
  const [glowIndex, setGlowIndex] = useState<number | null>(null);

  useEffect(() => {
    if (latest === null) return;
    setGlowIndex(latest);
    const timer = window.setTimeout(() => setGlowIndex(null), 1400);
    return () => window.clearTimeout(timer);
  }, [latest]);

  const progressPercent = LIFECYCLE.length ? (revealed.size / LIFECYCLE.length) * 100 : 0;

  return (
    <section className="border-y border-[#E5E5E0] bg-white">
      <div className="mx-auto max-w-[1200px] px-5 py-20">
        <p className="mb-2 text-[12px] font-medium uppercase tracking-[0.08em] text-[#6B7280]">
          A personal inventory for every stage
        </p>
        <h2 className="mb-12 font-editorial text-[clamp(1.8rem,3vw,2.4rem)] leading-[1.1]">
          You bought a thing. Now what?
        </h2>

        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-px md:hidden">
            <div className="absolute inset-0 bg-[#E5E5E0]" />
            <div
              className="absolute inset-x-0 top-0 bg-[#0F766E] transition-all duration-700 ease-out"
              style={{ height: `${progressPercent}%` }}
            />
          </div>

          <div className="absolute left-[10%] right-[10%] top-[52px] hidden h-px md:block">
            <div className="absolute inset-0 bg-[#E5E5E0]" />
            <div
              className="absolute inset-y-0 left-0 bg-[#0F766E] transition-all duration-700 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-5">
            {LIFECYCLE.map((item, i) => {
              const Icon = item.icon;
              const isRevealed = revealed.has(i);
              const isGlowing = glowIndex === i;

              return (
                <div
                  key={item.label}
                  ref={setRef(i)}
                  className={cn(
                    "relative z-10 flex flex-col gap-3 rounded-[16px] border bg-white p-5 will-change-[transform,opacity]",
                    isRevealed
                      ? "translate-y-0 scale-100 opacity-100"
                      : "translate-y-16 scale-[0.90] opacity-0",
                  )}
                  style={{
                    transition: isRevealed
                      ? `opacity 900ms cubic-bezier(0.22,1,0.36,1) ${i * 160}ms, transform 1000ms cubic-bezier(0.22,1,0.36,1) ${i * 160}ms, border-color 400ms ease, box-shadow 400ms ease`
                      : "none",
                    borderColor: isGlowing ? "rgba(15,118,110,0.42)" : "#E5E5E0",
                    boxShadow: isGlowing ? "0 0 0 1px rgba(15,118,110,0.08), 0 10px 24px rgba(15,118,110,0.08)" : "none",
                  }}
                >
                  <div
                    className="mb-1 flex h-10 w-10 items-center justify-center rounded-xl transition-transform duration-500 ease-out"
                    style={{
                      backgroundColor: `${item.pillBg}15`,
                      transform: isRevealed ? "scale(1)" : "scale(0.82)",
                      transitionDelay: isRevealed ? `${i * 110 + 120}ms` : "0ms",
                    }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} style={{ color: item.pillBg }} />
                  </div>

                  <span
                    className="self-start rounded-full px-2.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.1em] text-white"
                    style={{ backgroundColor: item.pillBg }}
                  >
                    {item.label}
                  </span>

                  <div>
                    <h3 className="mb-1 text-[14px] font-semibold leading-snug text-[#111]">{item.title}</h3>
                    <p className="text-[12.5px] leading-relaxed text-[#6B7280]">{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
